import { TypesOfShippings, ShippingEstimate } from '@pedix-workspace/utils';
import {
  ShippingCostResponse,
  ParcelShippingResponse,
  ResponseWithDeliveryCost,
  ResponseWithMinimumPurchaseForFreeShipping,
  ResponseWithDeliveryCostToBeDefined,
  ResponseWithFreeShippingGrantedAmount,
  ResponseWithMinimumPurchaseErrorAmount,
  ResponseWithOutOfDeliveryZone,
  ResponseWithPostalCodeRequired,
  ResponseWithAvailableParcelOptions,
  ResponseWithShippingEstimate,
  ResponseRequiringManualConfirmation,
  ResponseWithShippingOptions,
  ShippingCostResponseDelivery,
  ShippingCostResponseParcel,
} from './shipping-cost-response.types';

export function isShippingCostResponseDelivery(
  response: ShippingCostResponse | null | undefined,
): response is ShippingCostResponseDelivery {
  return response?.shippingOption.type === TypesOfShippings.DELIVERY;
}

export function isShippingCostResponseParcel(
  response: ShippingCostResponse | null | undefined,
): response is ShippingCostResponseParcel {
  return response?.shippingOption.type === TypesOfShippings.PARCEL;
}
/**
 * Verifica si la respuesta tiene un costo de envío definido y mayor a 0
 */
export function hasDeliveryCost(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithDeliveryCost {
  if (!response) return false;
  return 'deliveryCost' in response && response.deliveryCost > 0;
}

/**
 * Verifica si la respuesta tiene información sobre mínimo de compra para envío gratis
 */
export function hasMinimumPurchaseForFreeShipping(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithMinimumPurchaseForFreeShipping {
  if (!response) return false;
  return (
    'minimumPurchaseForFreeShipping' in response &&
    response.minimumPurchaseForFreeShipping !== null &&
    response.minimumPurchaseForFreeShipping !== undefined
  );
}

/**
 * Verifica si la respuesta indica que el costo está por definir
 */
export function hasDeliveryCostToBeDefined(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithDeliveryCostToBeDefined {
  if (!response) return false;
  return 'deliveryCostToBeDefined' in response && response.deliveryCostToBeDefined === true;
}

/**
 * Verifica si la respuesta otorga envío gratis
 */
export function hasFreeShippingGrantedAmount(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithFreeShippingGrantedAmount {
  if (!response) return false;
  return (
    'freeShippingGrantedAmount' in response &&
    response.freeShippingGrantedAmount !== null &&
    response.freeShippingGrantedAmount !== undefined
  );
}

/**
 * Verifica si la respuesta indica error por mínimo de compra no alcanzado
 */
export function hasMinimumPurchaseErrorAmount(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithMinimumPurchaseErrorAmount {
  if (!response) return false;
  return (
    'minimumPurchaseErrorAmount' in response &&
    response.minimumPurchaseErrorAmount !== null &&
    response.minimumPurchaseErrorAmount !== undefined
  );
}

/**
 * Verifica si la respuesta tiene una estimación de envío
 */
export function hasShippingEstimate(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithShippingEstimate {
  if (!response) return false;
  return 'shippingEstimate' in response && !!response.shippingEstimate;
}

/**
 * Verifica si la respuesta indica que el pedido debe bloquear por la estimación
 */
export function hasShippingEstimateBlockOrder(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithShippingEstimate & {
  shippingEstimate: ShippingEstimate & { blockOrder: boolean };
} {
  if (!response) return false;
  return (
    'shippingEstimate' in response &&
    'blockOrder' in response.shippingEstimate &&
    !!response.shippingEstimate.blockOrder
  );
}

/**
 * Verifica si la respuesta tiene un error en la estimación de envío
 */
export function hasShippingEstimateError(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithShippingEstimate & {
  shippingEstimate: ShippingEstimate & { error: string };
} {
  if (!response) return false;
  return (
    'shippingEstimate' in response &&
    'error' in response.shippingEstimate &&
    !!response.shippingEstimate.error
  );
}

/**
 * Verifica si la respuesta indica que está fuera de zona de entrega
 */
export function hasOutOfDeliveryZone(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithOutOfDeliveryZone {
  if (!response) return false;
  return 'outOfDeliveryZone' in response && !!response.outOfDeliveryZone;
}

/**
 * Verifica si la respuesta requiere código postal
 */
export function hasPostalCodeRequired(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithPostalCodeRequired {
  if (!response) return false;
  return 'postalCodeRequired' in response && !!response.postalCodeRequired;
}

/**
 * Verifica si la respuesta tiene opciones de paquetería disponibles
 */
export function hasAvailableParcelOptions(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithAvailableParcelOptions {
  if (!response) return false;
  return 'availableParcelOptions' in response;
}

/**
 * Verifica si la respuesta es de tipo parcel-shipping (útil para templates)
 */
export function isParcelShippingResponse(
  response: ShippingCostResponse | null | undefined,
): response is ParcelShippingResponse {
  return response?.type === 'parcel-shipping';
}
/**
 * Verifica si la respuesta requiere confirmación manual del usuario
 * Esto incluye: costos por definir, errores en estimaciones, o fuera de zona
 */
export function requiresManualConfirmation(
  response: ShippingCostResponse | null | undefined,
): response is ResponseRequiringManualConfirmation {
  if (!response) return false;

  if (response.type === 'to-be-defined') return true;

  if (hasShippingEstimateError(response)) return true;

  if (hasOutOfDeliveryZone(response)) return true;

  return false;
}

/**
 * Verifica si la respuesta contiene opciones de envío disponibles
 * Esto incluye: estimaciones de envío o opciones de paquetería
 */
export function hasShippingOptions(
  response: ShippingCostResponse | null | undefined,
): response is ResponseWithShippingOptions {
  if (!response) return false;

  // Tiene estimación de envío con datos
  if ('shippingEstimate' in response && !!response.shippingEstimate) return true;

  // Tiene opciones de paquetería disponibles
  if (hasAvailableParcelOptions(response) && response.availableParcelOptions.length > 0) {
    return true;
  }

  return false;
}

export function shouldFallbackToParcelShipping(
  response: ShippingCostResponse | null | undefined,
): boolean {
  if (!response) return false;
  return (
    hasShippingEstimateError(response) ||
    hasOutOfDeliveryZone(response) ||
    hasShippingEstimateBlockOrder(response)
  );
}
